import axios, { AxiosError } from 'axios';
import { API_BASE_URL, API_TIMEOUT } from '@/utils/constants';

export interface PublicOpsPlanPayload {
  share: { recipientName: string; recipientType: 'CONTRACTOR' | 'EXECUTIVE'; expiresAt: string };
  activity: {
    title: string;
    activityType: string;
    status: string;
    description: string | null;
    startDatetime: string | null;
    endDatetime: string | null;
    location: string | null;
    locationLatitude: number | null;
    locationLongitude: number | null;
    securityRequirements?: string | null;
    medical?: {
      closestHospitalName: string | null;
      closestHospitalAddress: string | null;
      closestHospitalPhone: string | null;
      closestHospitalDistance: string | null;
      closestHospitalTravelTime: string | null;
      closestHospitalCapabilities: string | null;
      alternativeMedicalFacilities: string | null;
      onsiteMedicalSupport: string | null;
    };
    evacuationRoute?: string | null;
    riskAssessment?: string | null;
  };
  venue: {
    name: string;
    address: string;
    latitude: number | null;
    longitude: number | null;
    securityContactName: string | null;
    securityContactPhone: string | null;
  } | null;
  attachments: Array<{ id: string; originalFilename: string; mimeType: string; fileSize: number }>;
}

const publicClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
});

interface Envelope<T> { success: boolean; data?: T; error?: { code: string; message: string }; }

const SHARE_UNAVAILABLE = 'SHARE_UNAVAILABLE';

export class ShareUnavailableError extends Error {
  constructor() {
    super('This link is no longer available.');
    this.name = 'ShareUnavailableError';
  }
}

function rethrow(err: unknown): never {
  if (err instanceof AxiosError) {
    const data = err.response?.data as Envelope<unknown> | undefined;
    if (data?.error?.code === SHARE_UNAVAILABLE) { throw new ShareUnavailableError(); }
    if (err.response?.status === 429) {
      throw new Error('Too many attempts. Please wait a few minutes and try again.');
    }
    if (data?.error?.message) { throw new Error(data.error.message); }
  }
  throw err instanceof Error ? err : new Error('Request failed');
}

export async function requestOtp(token: string): Promise<void> {
  try {
    await publicClient.post(`/api/v1/public/ops-plan/${token}/verify`, {});
  } catch (err) {
    rethrow(err);
  }
}

export async function exchangeOtp(
  token: string,
  otp: string
): Promise<{ accessToken: string; expiresAt: string }> {
  try {
    const response = await publicClient.post<Envelope<{ accessToken: string; expiresAt: string }>>(
      `/api/v1/public/ops-plan/${token}/access`,
      { otp }
    );
    if (!response.data.success || !response.data.data) { throw new ShareUnavailableError(); }
    return response.data.data;
  } catch (err) {
    rethrow(err);
  }
}

export async function fetchOpsPlan(
  token: string,
  accessToken: string
): Promise<PublicOpsPlanPayload> {
  try {
    const response = await publicClient.get<Envelope<PublicOpsPlanPayload>>(
      `/api/v1/public/ops-plan/${token}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (!response.data.success || !response.data.data) { throw new ShareUnavailableError(); }
    return response.data.data;
  } catch (err) {
    rethrow(err);
  }
}

export async function downloadSharedAttachment(
  token: string,
  accessToken: string,
  attachmentId: string,
  filename: string
): Promise<void> {
  try {
    const response = await publicClient.get<Blob>(
      `/api/v1/public/ops-plan/${token}/attachments/${attachmentId}`,
      { headers: { Authorization: `Bearer ${accessToken}` }, responseType: 'blob' }
    );
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    rethrow(err);
  }
}
